import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { outsideOf, useDismissLayer } from "../lib/dismissLayers";
import { PHONE_QUERY, useMediaQuery } from "../lib/useMediaQuery";

interface PopoverProps {
  /** Visible button content. */
  button: ReactNode;
  /** Accessible name of the panel. */
  label: string;
  buttonClassName?: string;
  buttonLabel?: string;
  align?: "start" | "end";
  children: (close: () => void) => ReactNode;
  /** Increment to open from outside (keyboard shortcut). */
  openSignal?: number;
  /** `data-guide` on the root, so a guided tour can point at the button (features/guides). */
  guide?: string;
  /** Open on first render (batch 16: a filter just added through More filters opens its editor at once). */
  initiallyOpen?: boolean;
  /**
   * D-B18-04: on desktop, place the panel against the viewport (`position: fixed`, below the button, or above it when there is
   * more room there) instead of inside the nearest positioned ancestor, so a scroll container around the trigger cannot clip it.
   * The board card menu needs it: `.gf-board` scrolls sideways, which also clips vertically, and the menu's "Not available to
   * you" group fell below the board's bottom edge. Off by default, so no other popover moves. Batch 25 GRF-84: the person bar's
   * "+N more" panel floats too, and a floating panel is clamped to the window on both sides.
   */
  floating?: boolean;
}

/** The viewport room kept around a floating panel. */
const FLOAT_GAP = 6;
const FLOAT_MARGIN = 12;

function focusById(id: string): void {
  document.getElementById(id)?.focus();
}

/**
 * Disclosure button with a floating panel. Esc or a click outside closes it (Esc returns focus to the button). It is
 * a layer of the shared dismiss stack (shared/lib/dismissLayers), so inside a dialog the first Esc or outside click
 * closes only the popover.
 * Entries inside are ordinary buttons and links, so disabled entries can carry aria-describedby reasons.
 * Below 760 px the panel is a bottom sheet rendered into document.body, so no card, column or scroll container
 * can paint over it (found by the 390 px Playwright run on the Kanban board).
 */
export function Popover({ button, label, buttonClassName = "btn sec", buttonLabel, align = "end", children, openSignal, guide, initiallyOpen = false, floating = false }: PopoverProps) {
  const [open, setOpen] = useState(initiallyOpen);
  const [seenSignal, setSeenSignal] = useState(openSignal);
  const panelId = useId();
  const buttonId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const phone = useMediaQuery(PHONE_QUERY);

  // Opening from a shortcut: adjust state while rendering, not in an effect.
  if (openSignal !== seenSignal) {
    setSeenSignal(openSignal);
    if (openSignal) setOpen(true);
  }

  useEffect(() => {
    if (!open) return;
    document.getElementById(panelId)?.querySelector<HTMLElement>("button:not([aria-disabled='true']), a, input, [tabindex='0']")?.focus();
  }, [open, panelId]);

  // D-B18-04: a floating panel follows its button while the page or any scroll container moves, and on resize.
  useLayoutEffect(() => {
    if (!open || phone || !floating) return;
    const place = () => {
      const trigger = document.getElementById(buttonId);
      const panel = panelRef.current;
      if (!trigger || !panel) return;
      const box = trigger.getBoundingClientRect();
      const below = window.innerHeight - box.bottom - FLOAT_GAP - FLOAT_MARGIN;
      const above = box.top - FLOAT_GAP - FLOAT_MARGIN;
      const upward = below < Math.min(panel.scrollHeight, 320) && above > below;
      panel.style.position = "fixed";
      panel.style.top = upward ? "auto" : `${box.bottom + FLOAT_GAP}px`;
      panel.style.bottom = upward ? `${window.innerHeight - box.top + FLOAT_GAP}px` : "auto";
      panel.style.maxHeight = `${Math.max(120, upward ? above : below)}px`;
      // Batch 25 GRF-84: clamped on both sides, so a wide panel opened near the window's edge never runs past it.
      const room = Math.max(0, window.innerWidth - panel.offsetWidth - FLOAT_MARGIN);
      if (align === "end") {
        panel.style.left = "auto";
        panel.style.right = `${Math.min(room, Math.max(FLOAT_MARGIN, window.innerWidth - box.right))}px`;
      } else {
        panel.style.right = "auto";
        panel.style.left = `${Math.min(room, Math.max(FLOAT_MARGIN, box.left))}px`;
      }
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open, phone, floating, align, buttonId]);

  // Batch 25 GRF-84: an anchored (not floating) desktop panel is shifted sideways, never past the window's edge: a "start"
  // panel opened from a button near the right edge used to run off the screen (and widen the page). Measured with the shift
  // removed, so it settles again after a resize; the panel keeps its place under the button otherwise.
  useLayoutEffect(() => {
    if (!open || phone || floating) return;
    const keepInside = () => {
      const panel = panelRef.current;
      if (!panel) return;
      panel.style.translate = "";
      const box = panel.getBoundingClientRect();
      const overRight = box.right - (window.innerWidth - FLOAT_MARGIN);
      const overLeft = FLOAT_MARGIN - box.left;
      const shift = overRight > 0 ? -Math.min(overRight, Math.max(0, box.left - FLOAT_MARGIN)) : overLeft > 0 ? overLeft : 0;
      if (shift !== 0) panel.style.translate = `${Math.round(shift)}px 0`;
    };
    keepInside();
    window.addEventListener("resize", keepInside);
    // Content that arrives after opening (a list that loads) can widen the panel.
    const observer = typeof ResizeObserver === "undefined" || !panelRef.current ? null : new ResizeObserver(keepInside);
    if (observer && panelRef.current) observer.observe(panelRef.current);
    return () => {
      window.removeEventListener("resize", keepInside);
      observer?.disconnect();
    };
  }, [open, phone, floating]);

  useDismissLayer(open, {
    element: () => rootRef.current,
    isOutside: (event) => outsideOf(event, rootRef.current, panelRef.current),
    dismiss: (reason) => {
      setOpen(false);
      if (reason === "escape") focusById(buttonId);
    },
  });

  const close = () => {
    setOpen(false);
    focusById(buttonId);
  };

  const panel = open ? (
    <div ref={panelRef} id={panelId} className={phone ? "gf-pop-panel sheet" : `gf-pop-panel ${align}${floating ? " floating" : ""}`} role="group" aria-label={label}>
      {children(close)}
    </div>
  ) : null;

  return (
    <div className="gf-pop" ref={rootRef} data-guide={guide}>
      <button
        id={buttonId}
        type="button"
        className={buttonClassName}
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={buttonLabel}
        onClick={() => setOpen((v) => !v)}
      >
        {button}
      </button>
      {panel && (phone ? createPortal(panel, document.body) : panel)}
    </div>
  );
}
